import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MsalModule, MsalRedirectComponent } from '@azure/msal-angular';
import { ActionReducer, MetaReducer, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { localStorageSync } from 'ngrx-store-localstorage';
import { OrderModule } from 'ngx-order-pipe';
import { NgxPaginationModule } from 'ngx-pagination';
import { SwiperModule } from 'swiper/angular';
import { AppRoutingModule } from './app-routing.module';
import { MSALProviders } from './app.azure';
import { AppComponent } from './app.component';
import { AddTokenService } from './auth/interceptor/add-token/add-token.service';
import { BaseUrlService } from './interceptors/base-url/base-url.service';
import { DateFromBackPipe } from './pipes/date-from-back.pipe';
import { FilterArrayPipe } from './pipes/filter-array.pipe';
import { FilterTablePipe } from './pipes/filter-table.pipe';
import { FirstToUpperinPhrasePipe } from './pipes/first-to-upperin-phrase.pipe';
import { PaginatePipe } from './pipes/paginate.pipe';
import { ParseRutPipe } from './pipes/parse-rut.pipe';
import { SeparatedByCharacterPipe } from './pipes/separated-by-character.pipe';
import { TableSortingPipe } from './pipes/table-sorting.pipe';
import { AttachDocumentComponent } from './shared/attach-document/attach-document.component';
import { BreadcrumComponent } from './shared/breadcrum/breadcrum.component';
import { CalendarComponent } from './shared/calendar/calendar.component';
import { DocumentTableComponent } from './shared/document-table/document-table.component';
import { EquipmentRowComponent } from './shared/equipment-row/equipment-row.component';
import { ErrorMessageComponent } from './shared/error-message/error-message.component';
import { FilterCheckboxComponent } from './shared/filter-checkbox/filter-checkbox.component';
import { FlowStatesComponent } from './shared/flow-states/flow-states.component';
import { FooterComponent } from './shared/footer/footer.component';
import { InfractionsViewComponent } from './shared/infractions-view/infractions-view.component';
import { InfringementCardComponent } from './shared/infringement-card/infringement-card.component';
import { InputComponent } from './shared/input/input.component';
import { LicenseCardComponent } from './shared/license-card/license-card.component';
import { LicenseDetailComponent } from './shared/license-detail/license-detail.component';
import { LoaderComponent } from './shared/loader/loader.component';
import { MineRequirementTableComponent } from './shared/mine-requirement-table/mine-requirement-table.component';
import { ModalComponent } from './shared/modal/modal.component';
import { NavbarComponent } from './shared/navbar/navbar.component';
import { PageLoaderComponent } from './shared/page-loader/page-loader.component';
import { PersonCardComponent } from './shared/person-card/person-card.component';
import { PersonFinderComponent } from './shared/person-finder/person-finder.component';
import { SelectDropDownListComponent } from './shared/select-drop-down-list/select-drop-down-list.component';
import { SelectListComponent } from './shared/select-list/select-list.component';
import { SelectPersonComponent } from './shared/select-person/select-person.component';
import { SelectComponent } from './shared/select/select.component';
import { TableGreyComponent } from './shared/table-grey/table-grey.component';
import { TableOrangeComponent } from './shared/table-orange/table-orange.component';
import { TableSortingArrowComponent } from './shared/table-sorting-arrow/table-sorting-arrow.component';
import { TableComponent } from './shared/table/table.component';
import { TextAreaComponent } from './shared/text-area/text-area.component';
import { TooltipComponent } from './shared/tooltip/tooltip.component';
import { TransactionResultViewComponent } from './shared/transaction-result-view/transaction-result-view.component';
import { rootReducer } from './store';
import { AuthFailureComponent } from './views/auth-failure/auth-failure.component';
import { AuthenticatingComponent } from './views/authenticating/authenticating.component';
import { LoginComponent } from './views/login/login.component';
import { ActionButtonsComponent } from './views/main/dashboard/action-buttons/action-buttons.component';
import { ApproverComponent } from './views/main/dashboard/banner-actions/approver/approver.component';
import { BannerObjectComponent } from './views/main/dashboard/banner-actions/banner-object/banner-object.component';
import { InfractionManagerComponent } from './views/main/dashboard/banner-actions/infraction-manager/infraction-manager.component';
import { LicenseComponent as BannerLicenseComponent } from './views/main/dashboard/banner-actions/license/license.component';
import { MineLicenseManagerComponent } from './views/main/dashboard/banner-actions/mine-license-manager/mine-license-manager.component';
import { NoLicenseComponent } from './views/main/dashboard/banner-actions/no-license/no-license.component';
import { DashboardComponent } from './views/main/dashboard/dashboard.component';
import { TableRecentActivitiesComponent } from './views/main/dashboard/table-recent-activities/table-recent-activities.component';
import { TableRecentRequestComponent } from './views/main/dashboard/table-recent-request/table-recent-request.component';
import { DelegateHomeComponent } from './views/main/delegate/delegate-home/delegate-home.component';
import { DelegateComponent } from './views/main/delegate/delegate.component';
import { FailureComponent } from './views/main/delegate/failure/failure.component';
import { SuccessComponent } from './views/main/delegate/success/success.component';
import { FaqEquipmentComponent } from './views/main/faq/faq-equipment/faq-equipment.component';
import { FaqHomeComponent } from './views/main/faq/faq-home/faq-home.component';
import { FaqInfrigementComponent } from './views/main/faq/faq-infrigement/faq-infrigement.component';
import { FaqLicenseComponent } from './views/main/faq/faq-license/faq-license.component';
import { FaqRequestComponent } from './views/main/faq/faq-request/faq-request.component';
import { FaqRequirementsComponent } from './views/main/faq/faq-requirements/faq-requirements.component';
import { FaqRulesOfProcedureComponent } from './views/main/faq/faq-rules-of-procedure/faq-rules-of-procedure.component';
import { FaqComponent } from './views/main/faq/faq.component';
import { EnterComponent } from './views/main/infractions/enter/enter.component';
import { FailureComponent as InfractionNewFailureComponent } from './views/main/infractions/enter/failure/failure.component';
import { FormDetailComponent } from './views/main/infractions/enter/form/form-detail/form-detail.component';
import { FormLocationAndEquipmentComponent } from './views/main/infractions/enter/form/form-location-and-equipment/form-location-and-equipment.component';
import { FormSelectPersonComponent } from './views/main/infractions/enter/form/form-select-person/form-select-person.component';
import { FormTypeSanctionComponent } from './views/main/infractions/enter/form/form-type-sanction/form-type-sanction.component';
import { ModalSanctionTypeMelComponent } from './views/main/infractions/enter/form/form-type-sanction/modal-sanction-type-mel/modal-sanction-type-mel.component';
import { FormComponent } from './views/main/infractions/enter/form/form.component';
import { SuccessComponent as InfractionNewSuccessComponent } from './views/main/infractions/enter/success/success.component';
import { InfractionsComponent } from './views/main/infractions/infractions.component';
import { ApproveComponent } from './views/main/license/approve/approve.component';
import { AdditionalInfoComponent } from './views/main/license/approve/detail/additional-info/additional-info.component';
import { ApproveRequestComponent } from './views/main/license/approve/detail/approve-request/approve-request.component';
import { ResultComponent as ApproveResultApproveRequestComponent } from './views/main/license/approve/detail/approve-request/result/result.component';
import { DelegateComponent as ApproveDelegateComponent } from './views/main/license/approve/detail/delegate/delegate.component';
import { ResultComponent as ApproveResultDelegateComponent } from './views/main/license/approve/detail/delegate/result/result.component';
import { DetailComponent as ApproveDetailComponent } from './views/main/license/approve/detail/detail.component';
import { RejectComponent } from './views/main/license/approve/detail/reject/reject.component';
import { ResultComponent as ApproveResultRejectComponent } from './views/main/license/approve/detail/reject/result/result.component';
import { RequestMoreInfoComponent } from './views/main/license/approve/detail/request-more-info/request-more-info.component';
import { ResultComponent as ApproveResultRequestMoreInfoComponent } from './views/main/license/approve/detail/request-more-info/result/result.component';
import { PendingsListComponent } from './views/main/license/approve/pendings-list/pendings-list.component';
import {
  LicenseComponent,
  LicenseMineComponent,
  LicenseMineUpdateComponent,
  LicenseNewComponent,
  LicenseNewDetailComponent,
  LicenseNewHomeComponent,
  LicenseNewRequestComponent,
  LicenseRenewComponent,
} from './views/main/license/license.module';
import { DetailComponent as LicenseMineUpdateDetailComponent } from './views/main/license/mine/update/detail/detail.component';
import { FormSelectPersonComponent as UpdateFormSelectPersonComponent } from './views/main/license/mine/update/form-select-person/form-select-person.component';
import { ResultComponent as UpdateLicenseMineComponent } from './views/main/license/mine/update/result/result.component';
import { ShowComponent } from './views/main/license/show/show.component';
import { ModalDetailActivitiesComponent } from './views/main/recent-activities/modal-detail-activities/modal-detail-activities.component';
import { RecentActivitiesComponent } from './views/main/recent-activities/recent-activities.component';
import { InfractionsComponent as ReportInfractionsComponent } from './views/main/reports/infractions/infractions.component';
import { ReportTableInfractionsComponent } from './views/main/reports/infractions/report-table-infractions/report-table-infractions.component';
import { LicensesComponent as ReportLicensesComponent } from './views/main/reports/licenses/licenses.component';
import { ReportTableLicencesComponent } from './views/main/reports/licenses/report-table-licences/report-table-licences.component';
import { ReportsComponent } from './views/main/reports/reports.component';
import { ReportTableRequestsComponent } from './views/main/reports/requests/report-table-requests/report-table-requests.component';
import { RequestsComponent as ReportRequestsComponent } from './views/main/reports/requests/requests.component';
import { DriverComponent } from './views/main/search/driver/driver.component';
import { HomeComponent } from './views/main/search/driver/home/home.component';
import { HomeComponent as DriverResultHomeComponent } from './views/main/search/driver/result/home/home.component';
import { InfractionsComponent as SearchDriverResultInfractionsComponent } from './views/main/search/driver/result/home/infractions/infractions.component';
import { DeleteEquipmentComponent } from './views/main/search/driver/result/home/licenses/delete-equipment/delete-equipment.component';
import { HomeComponent as SearchDriverResultLicensesHomeComponent } from './views/main/search/driver/result/home/licenses/home/home.component';
import { SuspensionModalComponent } from './views/main/search/driver/result/home/licenses/home/suspension-modal/suspension-modal.component';
import { LicensesComponent } from './views/main/search/driver/result/home/licenses/licenses.component';
import { ValidityComponent } from './views/main/search/driver/result/home/licenses/validity/validity.component';
import { ResultActionsComponent } from './views/main/search/driver/result/result-actions/result-actions.component';
import { ResultComponent } from './views/main/search/driver/result/result.component';
import { SearchComponent } from './views/main/search/search.component';
import { BackgroundUpdateComponent } from './views/main/tasks/background-update/background-update.component';
import { DetailComponent as TaskDetailComponent } from './views/main/tasks/detail/detail.component';
import { FailureTaskUpdateComponent } from './views/main/tasks/failure-task-update/failure-task-update.component';
import { SuccessTaskUpdateComponent } from './views/main/tasks/success-task-update/success-task-update.component';
import { TaskListComponent } from './views/main/tasks/task-list/task-list.component';
import { TasksComponent } from './views/main/tasks/tasks.component';
import { DeleteResultComponent } from './views/main/verification/delete-result/delete-result.component';
import { HomeComponent as VerificationHomeComponent } from './views/main/verification/home/home.component';
import { InfringementStatusComponent } from './views/main/verification/home/infringement-status/infringement-status.component';
import { DocumentsValidityComponent } from './views/main/verification/home/license-status/documents-validity/documents-validity.component';
import { LicenseStatusComponent } from './views/main/verification/home/license-status/license-status.component';
import { RequestStatusComponent } from './views/main/verification/home/request-status/request-status.component';
import { VerificationComponent } from './views/main/verification/verification.component';
import { ViewsComponent } from './views/views.component';

import { FormEmployeeComponent } from './views/main/license/extend/form/form-employee/form-employee.component';
import { DocumentsLoadComponent } from './views/main/license/extend/form/documents-load/documents-load.component';
import { FormComponent as LicenseExtendFormComponent } from './views/main/license/extend/form/form.component';
import { EquipmentComponent } from './views/main/license/extend/form/new-record/equipment/equipment.component';
import { LicenseComponent as LicenseExtendFormNewRecordLicenseComponent } from './views/main/license/extend/form/new-record/license/license.component';
import { NewRecordComponent } from './views/main/license/extend/form/new-record/new-record.component';
import { ZoneComponent } from './views/main/license/extend/form/new-record/zone/zone.component';
import { SelectLicenseComponent } from './views/main/license/extend/select-license/select-license.component';
import { SelectThirdComponent } from './views/main/license/extend/select-third/select-third.component';

import { ResultComponent as ExtendResultComponent } from './views/main/license/extend/result/result.component';
import { HomeComponent as ExtendHomeComponent } from './views/main/license/extend/home/home.component';
import { ExtendComponent } from './views/main/license/extend/extend.component';
import { FilterSelectorTableComponent } from './views/main/reports/filter-selector-table/filter-selector-table.component';

import { ResultComponent as RenewResultComponent } from './views/main/license/renew/result/result.component';
import { HomeComponent as RenewHomeComponent } from './views/main/license/renew/home/home.component';
import { FormComponent as RenewFormComponent } from './views/main/license/renew/form/form.component';
import { SelectLicenseComponent as RenewSelectLicenseComponent } from './views/main/license/renew/select-license/select-license.component';
import { SelectThirdComponent as RenewSelectThirdComponent } from './views/main/license/renew/select-third/select-third.component';
import { ContractSelectComponent } from './views/main/license/renew/form/contract-select/contract-select.component';
import { getEnvironment } from 'src/environments/dinamicEnv';
import { ModalErrorComponent } from './shared/modal-error/modal-error.component';
import { MulesoftErrorHandlerService } from './interceptors/mulesoft-error-handler/mulesoft-error-handler.service';
import { MulesoftHeadersService } from './interceptors/mulesoft-headers/mulesoft-headers.service';
import { ShowDeletedComponent } from './views/main/license/show-deleted/show-deleted.component';
import { HomeComponent as ShowDeletedHomeComponent } from './views/main/license/show-deleted/home/home.component';
import { ResultComponent as ShowDeletedResultComponent } from './views/main/license/show-deleted/result/result.component';
import { SummaryComponent as ShowDeletedResultSummaryComponent } from './views/main/license/show-deleted/result/summary/summary.component';
import { DetailComponent as ShowDeletedResultDetailComponent } from './views/main/license/show-deleted/result/detail/detail.component';
import { ResultComponent as NewLicenseRequestResultComponent } from './views/main/license/new/result/result.component';
import { DownloadDocumentComponent } from './shared/download-document/download-document.component';
import { FormAssetContractComponent } from './views/main/license/new/request/form-asset-contract/form-asset-contract.component';
import { FlowStateComponent } from './shared/flow-state/flow-state.component';
import { FlowStatesTooltipComponent } from './shared/flow-states-tooltip/flow-states-tooltip.component';
import { CookiesApproveAlertComponent } from './shared/cookies-approve-alert/cookies-approve-alert.component';
import { FormContractOwnerSelectorComponent } from './shared/form-contract-owner-selector/form-contract-owner-selector.component';
import { ModifyEquipmentFormComponent } from './views/main/search/driver/result/home/licenses/modify-equipment/modify-equipment-form/modify-equipment-form.component';
import { ModifyDatesFormComponent } from './views/main/search/driver/result/home/licenses/modify-equipment/modify-dates-form/modify-dates-form.component';
import { ModifyEquipmentComponent } from './views/main/search/driver/result/home/licenses/modify-equipment/modify-equipment.component';

export function localStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  return localStorageSync({ keys: ['login'] })(reducer);
}
const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer];

const environment = getEnvironment();

const MSALProvidersFiltered = [
  ...MSALProviders, {
    provide: HTTP_INTERCEPTORS,
    useClass: AddTokenService,
    multi: true,
  },
];
const msalBootstrap = [
  MsalRedirectComponent,
];
const msalImportModules = [
  MsalModule,
];

@NgModule({
  declarations: [
    AppComponent,
    NavbarComponent,
    FooterComponent,
    DashboardComponent,
    ApproverComponent,
    InfractionManagerComponent,
    BannerLicenseComponent,
    NoLicenseComponent,
    MineLicenseManagerComponent,
    DelegateComponent,
    PersonCardComponent,
    SuccessComponent,
    CalendarComponent,
    FailureComponent,
    DelegateHomeComponent,
    ModalComponent,
    BannerObjectComponent,
    InfractionsComponent,
    EnterComponent,
    TableSortingPipe,
    TableComponent,
    LoginComponent,
    RecentActivitiesComponent,
    SelectListComponent,
    ParseRutPipe,
    FilterTablePipe,
    TasksComponent,
    BackgroundUpdateComponent,
    TaskListComponent,
    SuccessTaskUpdateComponent,
    PaginatePipe,
    FailureTaskUpdateComponent,
    FlowStatesComponent,
    TooltipComponent,
    SeparatedByCharacterPipe,
    SeparatedByCharacterPipe,
    LoaderComponent,
    AttachDocumentComponent,
    FirstToUpperinPhrasePipe,
    PageLoaderComponent,
    // License
    LicenseComponent,
    LicenseRenewComponent,

    LicenseMineComponent,
    LicenseMineUpdateComponent,
    UpdateLicenseMineComponent,
    UpdateFormSelectPersonComponent,
    LicenseNewComponent,
    LicenseNewDetailComponent,
    LicenseNewHomeComponent,
    LicenseNewRequestComponent,
    TableRecentRequestComponent,
    TableRecentActivitiesComponent,
    LicenseCardComponent,
    LicenseDetailComponent,
    EquipmentRowComponent,
    DocumentTableComponent,
    TableSortingArrowComponent,
    TaskDetailComponent,
    FormLocationAndEquipmentComponent,
    FormDetailComponent,
    FormTypeSanctionComponent,
    FormSelectPersonComponent,
    PersonFinderComponent,
    TextAreaComponent,
    ErrorMessageComponent,
    ModalSanctionTypeMelComponent,
    TableOrangeComponent,
    TableGreyComponent,
    DateFromBackPipe,
    FormComponent,
    InfractionNewFailureComponent,
    InfractionNewSuccessComponent,
    ModalDetailActivitiesComponent,
    SelectPersonComponent,
    SearchComponent,
    DriverComponent,
    FaqComponent,
    FaqRequestComponent,
    FaqRequirementsComponent,
    FaqInfrigementComponent,
    FaqLicenseComponent,
    FaqEquipmentComponent,
    FaqRulesOfProcedureComponent,
    FaqHomeComponent,
    HomeComponent,
    ResultComponent,
    LicensesComponent,
    SearchDriverResultInfractionsComponent,
    DeleteEquipmentComponent,
    ValidityComponent,
    SearchDriverResultLicensesHomeComponent,
    ViewsComponent,
    InfringementCardComponent,
    ApproveComponent,
    PendingsListComponent,
    ApproveDetailComponent,
    ApproveRequestComponent,
    ApproveDelegateComponent,
    RejectComponent,
    RequestMoreInfoComponent,
    TransactionResultViewComponent,
    ApproveResultDelegateComponent,
    ApproveResultApproveRequestComponent,
    ApproveResultRequestMoreInfoComponent,
    ApproveResultRejectComponent,
    MineRequirementTableComponent,
    BreadcrumComponent,
    ShowComponent,
    AdditionalInfoComponent,
    InputComponent,
    LicenseMineUpdateDetailComponent,
    ReportsComponent,
    ReportRequestsComponent,
    ReportLicensesComponent,
    ReportInfractionsComponent,
    VerificationComponent,
    LicenseStatusComponent,
    InfringementStatusComponent,
    RequestStatusComponent,
    SelectDropDownListComponent,
    FilterArrayPipe,
    DeleteResultComponent,
    VerificationHomeComponent,
    SelectComponent,
    SuspensionModalComponent,
    ActionButtonsComponent,
    ResultActionsComponent,
    DriverResultHomeComponent,
    AuthFailureComponent,
    AuthenticatingComponent,
    DocumentsValidityComponent,
    InfractionsViewComponent,

    SelectThirdComponent,
    SelectLicenseComponent,
    FilterCheckboxComponent,
    ReportTableLicencesComponent,
    ReportTableInfractionsComponent,
    ReportTableRequestsComponent,
    FilterSelectorTableComponent,

    DocumentsLoadComponent,
    EquipmentComponent,
    ZoneComponent,
    NewRecordComponent,

    ExtendComponent,
    ExtendHomeComponent,
    LicenseExtendFormComponent,
    LicenseExtendFormNewRecordLicenseComponent,
    ExtendResultComponent,

    FormEmployeeComponent,
    RenewResultComponent,
    RenewFormComponent,
    RenewSelectLicenseComponent,
    RenewSelectThirdComponent,
    RenewHomeComponent,
    ContractSelectComponent,
    ModalErrorComponent,
    ShowDeletedComponent,
    ShowDeletedHomeComponent,
    ShowDeletedResultComponent,
    ShowDeletedResultSummaryComponent,
    ShowDeletedResultDetailComponent,
    NewLicenseRequestResultComponent,
    DownloadDocumentComponent,
    FormAssetContractComponent,
    FlowStateComponent,
    FlowStatesTooltipComponent,
    CookiesApproveAlertComponent,
    FormContractOwnerSelectorComponent,
    ModifyEquipmentFormComponent,
    ModifyDatesFormComponent,
    ModifyEquipmentComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    ReactiveFormsModule,
    MatPaginatorModule,
    NgxPaginationModule,
    FormsModule,
    MatTableModule,
    MatSortModule,
    OrderModule,
    MatExpansionModule,
    StoreModule.forRoot(rootReducer, {
      metaReducers,
    }),
    BrowserAnimationsModule,
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: environment.production, // Restrict extension to log-only mode
    }),
    SwiperModule,
    MatSnackBarModule,
    ...msalImportModules,
    MatCheckboxModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BaseUrlService,
      multi: true,
    },
    ...MSALProvidersFiltered,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MulesoftHeadersService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MulesoftErrorHandlerService,
      multi: true,
    },
  ],
  bootstrap: [
    AppComponent,
    ...msalBootstrap,
  ],
})
export class AppModule { }
